import { FlaskConical, Play } from 'lucide-react'
import { StatusBadge } from '@/components/ui/status-badge'
import { useRunSeedTest } from '@/hooks/use-deliverability'
import type { SeedTestRun } from '@/lib/types-deliverability'

interface SeedTestPanelProps {
  testRuns: SeedTestRun[]
  isLoading: boolean
}

export function SeedTestPanel({ testRuns, isLoading }: SeedTestPanelProps): React.JSX.Element {
  const runTest = useRunSeedTest()

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0">
      <div className="flex items-center justify-between border-b border-surface-3 px-5 py-4">
        <div className="flex items-center gap-2">
          <FlaskConical className="h-5 w-5 text-text-secondary" />
          <h2 className="text-lg font-medium text-text-primary">Seed Tests</h2>
        </div>
        <button
          type="button"
          onClick={() => runTest.mutate()}
          disabled={runTest.isPending}
          className="inline-flex items-center gap-1.5 rounded-lg bg-admin-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-admin-600 disabled:opacity-50"
        >
          <Play className="h-4 w-4" />
          {runTest.isPending ? 'Sending...' : 'Run Seed Test'}
        </button>
      </div>

      <div className="p-5">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-admin-500 border-t-transparent" />
          </div>
        ) : testRuns.length === 0 ? (
          <p className="py-8 text-center text-text-secondary">
            No seed tests yet. Click &quot;Run Seed Test&quot; to start.
          </p>
        ) : (
          <div className="space-y-4">
            {testRuns.slice(0, 5).map((run) => (
              <SeedTestRunRow key={run.test_id} run={run} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function SeedTestRunRow({ run }: { run: SeedTestRun }): React.JSX.Element {
  const sentAt = new Date(run.sent_at)
  const inboxCount = run.results.filter((r) => r.result === 'inbox').length
  const spamCount = run.results.filter((r) => r.result === 'spam').length
  const pendingCount = run.results.filter((r) => r.result === 'pending').length

  return (
    <div className="rounded-lg border border-surface-2 p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-text-primary">Test {run.test_id.slice(0, 8)}</p>
          <p className="text-xs text-text-tertiary">
            {sentAt.toLocaleDateString()} {sentAt.toLocaleTimeString()} - {run.seed_count} seeds
          </p>
        </div>
        <div className="flex items-center gap-2">
          {inboxCount > 0 && <StatusBadge status="inbox">{inboxCount} inbox</StatusBadge>}
          {spamCount > 0 && <StatusBadge status="spam">{spamCount} spam</StatusBadge>}
          {pendingCount > 0 && <StatusBadge status="pending">{pendingCount} pending</StatusBadge>}
        </div>
      </div>

      {/* Per-seed results */}
      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {run.results.map((result) => (
          <div
            key={result.message_id}
            className="flex items-center justify-between rounded-md bg-surface-1 px-3 py-2"
          >
            <div className="min-w-0">
              <p className="truncate text-xs font-medium text-text-primary">{result.seed_email}</p>
              <p className="text-xs text-text-tertiary">{result.provider}</p>
            </div>
            <StatusBadge status={result.result} />
          </div>
        ))}
      </div>
    </div>
  )
}
